import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SubscriberConfigureDialog from "./SubscriberConfigureDialog";
import DeleteIcon from "../../../../common/Icons/DeleteIcon";

const defaultRows = [
	{
		id: 1,
		agencyName: "AG-12345",
		username: "admin@agency.com",
		password: "******",
	},
];

export default function CongfigureAddIns({
	name,
	subtitle,
	status,
	rows,
	configurationFields,
	disableDelete,
	disableActions,
	onAddConfiguration,
	onEditConfiguration,
	onDeleteConfiguration,
}) {
	const [localRows, setLocalRows] = useState(rows);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogMode, setDialogMode] = useState("add");
	const [selectedRowForEdit, setSelectedRowForEdit] = useState(null);
	const [saving, setSaving] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [rowToDelete, setRowToDelete] = useState(null);
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		setLocalRows(rows);
	}, [rows]);

	const resolvedFields = Array.isArray(configurationFields)
		? configurationFields.filter((field) => field?.keyName)
		: [];

	const getFieldLabel = (field) => field?.userLabel || field?.keyName || "";

	const isSecretField = (field) => {
		const combined = `${field?.keyName || ""} ${field?.userLabel || ""}`.toLowerCase();
		return (
			combined.includes("password") ||
			combined.includes("credential") ||
			combined.includes("secret") ||
			combined.includes("key")
		);
	};

	const getLegacyFieldValue = (row, field) => {
		const combined = `${field?.keyName || ""} ${field?.userLabel || ""}`.toLowerCase();
		if (combined.includes("agency")) return row?.agencyName;
		if (combined.includes("username") || combined.includes("user name")) return row?.username;
		if (isSecretField(field)) return row?.password;
		return "";
	};

	const getFieldValueFromRow = (row, field) => {
		const keyName = field?.keyName;
		const sourceValues = row?.sourceRecord?.values || row?.fields || {};
		const rawValue = sourceValues[keyName] ?? getLegacyFieldValue(row, field);
		if (rawValue === null || rawValue === undefined || rawValue === "") {
			return "Not Set";
		}

		if (isSecretField(field)) {
			return "******";
		}

		return String(rawValue);
	};

	const buildLocalRowFromState = (id, state) => ({
		id,
		configurationName: state.configurationName,
		fields: state.fields || {},
		sourceRecord: {
			id,
			configurationName: state.configurationName,
			values: state.fields || {},
		},
	});

	const handleOpenAddDialog = () => {
		if (disableActions) return;
		setDialogMode("add");
		setSelectedRowForEdit(null);
		setDialogOpen(true);
	};

	const handleOpenEditDialog = (row) => {
		if (disableActions) return;
		setDialogMode("edit");
		setSelectedRowForEdit({
			id: row.id,
			configurationName: row.configurationName || "",
			fields: row.sourceRecord?.values || row.fields || {},
		});
		setDialogOpen(true);
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
		setSelectedRowForEdit(null);
	};

	const handleDialogSave = async (formData) => {
		setSaving(true);
		try {
			if (dialogMode === "add") {
				const success = await onAddConfiguration(formData);
				if (success === false) return;
				const newId = Date.now();

				setLocalRows((prev) => [
					...prev,
					buildLocalRowFromState(newId, formData),
				]);
			} else {
				const success = await onEditConfiguration({
					...selectedRowForEdit,
					...formData,
				});
				if (success === false) return;

				setLocalRows((prev) =>
					prev.map((r) =>
						r.id === selectedRowForEdit.id
							? buildLocalRowFromState(selectedRowForEdit.id, formData)
							: r,
					),
				);
			}

			handleDialogClose();
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (row) => {
		if (disableDelete || disableActions) return;
		setRowToDelete(row);
		setDeleteConfirmOpen(true);
	};

	const confirmDelete = async () => {
		if (!rowToDelete) {
			setDeleteConfirmOpen(false);
			return;
		}

		setDeleting(true);
		try {
			const success = await onDeleteConfiguration(rowToDelete);
			if (success === false) {
				setDeleteConfirmOpen(false);
				setRowToDelete(null);
				return;
			}

			setLocalRows((prev) => prev.filter((r) => r.id !== rowToDelete.id));
			setDeleteConfirmOpen(false);
			setRowToDelete(null);
		} finally {
			setDeleting(false);
		}
	};

	const cancelDelete = () => {
		setDeleteConfirmOpen(false);
		setRowToDelete(null);
	};
	return (
		<Paper
			elevation={0}
			sx={{
				border: "1px solid #e6e9ef",
				borderRadius: 2,
				overflow: "hidden",
				backgroundColor: "#fff",
			}}
		>
			<Accordion disableGutters defaultExpanded elevation={0} sx={{ "&:before": { display: "none" } }}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon sx={{ color: "#7f8a9a" }} />}
					sx={{
						minHeight: 56,
						px: 2,
						"& .MuiAccordionSummary-content": {
							m: 0,
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						},
					}}
				>
					<Typography sx={{ fontSize: 14, fontWeight: 500, color: "#2f3a4a" }}>
						{name}
					</Typography>
				</AccordionSummary>

				<AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
						<Box>
							<Typography sx={{ fontSize: 18, fontWeight: 500, color: "#2f3a4a", lineHeight: 1.2 }}>
								{name}
							</Typography>
							<Typography sx={{ fontSize: 11, color: "#8b95a5", mt: 0.25 }}>
								{subtitle}
							</Typography>
						</Box>
						<Chip
							label={status}
							size="small"
							sx={{
								bgcolor: status === "Configured" ? "#e8f7ef" : "#fdf0eb",
								color: status === "Configured" ? "#5fae86" : "#b4532a",
								fontWeight: 600,
								height: 24,
								borderRadius: "12px",
								px: 0.5,
							}}
						/>
					</Box>

					<TableContainer
						sx={{
							border: "1px solid #edf0f4",
							borderRadius: 1.5,
							mb: 1.5,
						}}
					>
						<Table size="small" aria-label="configuration table" sx={{ marginTop: 0 }}>
							<TableHead>
								<TableRow sx={{ backgroundColor: "#f7f9fb" }}>
									<TableCell sx={{ fontSize: 10, color: "#8b95a5", py: 1 }}>CONFIGURATION NAME</TableCell>
									{resolvedFields.map((field) => (
										<TableCell key={field.keyName} sx={{ fontSize: 10, color: "#8b95a5", py: 1 }}>
											{getFieldLabel(field).toUpperCase()}
										</TableCell>
									))}
									<TableCell align="right" sx={{ fontSize: 10, color: "#8b95a5", py: 1 }}>ACTIONS</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{localRows.length === 0 && (
									<TableRow>
										<TableCell colSpan={resolvedFields.length + 2} sx={{ fontSize: 12, color: "#8b95a5", py: 2 }}>
											No configuration found.
										</TableCell>
									</TableRow>
								)}
								{localRows.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell sx={{ fontSize: 12, color: "#4c5666" }}>
											{row.configurationName || row.sourceRecord?.configurationName || "Not Set"}
										</TableCell>
										{resolvedFields.map((field) => (
											<TableCell key={`${row.id}-${field.keyName}`} sx={{ fontSize: 12, color: "#4c5666" }}>
												{getFieldValueFromRow(row, field)}
											</TableCell>
										))}
										<TableCell align="right" sx={{ py: 0.5 }}>
											<IconButton
												size="small"
												aria-label="edit-configuration"
												onClick={() => handleOpenEditDialog(row)}
												disabled={disableActions || saving}
											>
												<EditOutlinedIcon sx={{ fontSize: 18, color: "#3f7ddd" }}  />
											</IconButton>
											<IconButton
												size="small"
												aria-label="delete-configuration"
												onClick={() => handleDelete(row)}
												disabled={disableDelete || disableActions || saving}
											>
												<DeleteIcon color="#e55353"  width={15} height={15}/>
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>

					<Button
						variant="outlined"
						startIcon={<AddIcon />}
						onClick={handleOpenAddDialog}
						disabled={disableActions || saving}
						sx={{
							textTransform: "none",
							borderColor: "#d9dee8",
							color: "#5f6b7a",
							fontSize: 12,
							fontWeight: 500,
							px: 2,
							py: 0.6,
							"&:hover": {
								borderColor: "#bcc5d3",
								backgroundColor: "#f8fafc",
							},
						}}
					>
						Add Configuration
					</Button>
				</AccordionDetails>
			</Accordion>

			<SubscriberConfigureDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				onSave={handleDialogSave}
				saving={saving}
				mode={dialogMode}
				initialData={selectedRowForEdit}
				productName={name}
				configurationFields={configurationFields}
			/>

			<Dialog open={deleteConfirmOpen} onClose={cancelDelete} maxWidth="xs" fullWidth disableEscapeKeyDown={deleting}>
				<DialogTitle sx={{ fontWeight: 600, color: "#2f3a4a" }}>
					Delete Configuration
				</DialogTitle>
				<DialogContent>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 1 }}>
						{deleting && <CircularProgress size={40} />}
						<Typography sx={{ fontSize: 14, color: "#4c5666" }}>
							{deleting ? "Deleting configuration..." : "Are you sure you want to delete this configuration? This action cannot be undone."}
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions sx={{ p: 2, gap: 1 }}>
					<Button
						onClick={cancelDelete}
						variant="outlined"
						disabled={deleting}
						sx={{
							borderColor: "#d9dee8",
							color: "#5f6b7a",
							textTransform: "none",
							fontWeight: 500,
							"&:hover": {
								borderColor: "#bcc5d3",
								backgroundColor: "#f8fafc",
							},
							"&:disabled": {
								borderColor: "#d9dee8",
								color: "#a8b0ba",
							},
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={confirmDelete}
						variant="contained"
						disabled={deleting}
						sx={{
							backgroundColor: "#e55353",
							color: "#fff",
							textTransform: "none",
							fontWeight: 500,
							"&:hover": {
								backgroundColor: "#d93f3f",
							},
							"&:disabled": {
								backgroundColor: "#f0b8b8",
								color: "#fff",
							},
						}}
					>
						{deleting ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
}

CongfigureAddIns.propTypes = {
	name: PropTypes.string,
	subtitle: PropTypes.string,
	status: PropTypes.string,
	disableDelete: PropTypes.bool,
	disableActions: PropTypes.bool,
	configurationFields: PropTypes.arrayOf(
		PropTypes.shape({
			keyName: PropTypes.string,
			userLabel: PropTypes.string,
			readOnly: PropTypes.bool,
		}),
	),
	rows: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
			configurationName: PropTypes.string,
			agencyName: PropTypes.string,
			username: PropTypes.string,
			password: PropTypes.string,
			fields: PropTypes.object,
			sourceRecord: PropTypes.object,
		}),
	),
	onAddConfiguration: PropTypes.func,
	onEditConfiguration: PropTypes.func,
	onDeleteConfiguration: PropTypes.func,
};

CongfigureAddIns.defaultProps = {
	name: "AMS360",
	subtitle: "Internal Configuration",
	status: "Configured",
	disableDelete: false,
	disableActions: false,
	configurationFields: [],
	rows: defaultRows,
	onAddConfiguration: () => {},
	onEditConfiguration: () => {},
	onDeleteConfiguration: () => {},
};
